import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useGallery, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ─── Related-photo selection ────────────────────────────────────────────────
//
// Each tile should show a photo that actually matches its topic — an interior
// for Floor Plans, an amenity space for Amenities, a community/exterior shot
// for the Gallery — rather than an arbitrary sequential gallery index (which
// also left card 1 blank whenever photo_gallery[0] had no image). We score
// every gallery photo against per-card keywords and assign the best DISTINCT
// match to each card, then fall back to any remaining photo so no tile is ever
// empty. The editor can still override any card's image explicitly.

const FLOORPLAN_KEYWORDS = [
  'floor plan', 'floorplan', 'bedroom', 'interior', 'apartment', 'living',
  'kitchen', 'unit', 'model', 'bath', 'dining', 'closet', 'room', 'home',
];
const AMENITY_KEYWORDS = [
  'pool', 'gym', 'fitness', 'clubhouse', 'lounge', 'amenity', 'amenities',
  'spa', 'courtyard', 'grill', 'rooftop', 'recreation', 'theater', 'coworking',
  'game', 'yoga', 'lobby',
];
const COMMUNITY_KEYWORDS = [
  'exterior', 'building', 'community', 'entrance', 'facade', 'aerial',
  'landscape', 'neighborhood', 'street', 'courtyard', 'pool', 'sign', 'view',
];

const CARD_KEYWORDS = [FLOORPLAN_KEYWORDS, AMENITY_KEYWORDS, COMMUNITY_KEYWORDS];

function scorePhoto(photo: PhotoGallery, keywords: string[]): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return keywords.reduce((score, kw) => score + (text.includes(kw) ? 1 : 0), 0);
}

// Generator sentinels ("false", "null", …) are truthy strings but not usable
// image URLs — treat them as missing so the fallback chain advances.
function pickImage(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (['false', 'true', 'null', 'none', 'undefined'].includes(lower)) return null;
  return trimmed;
}

// ─── Slot group definitions ────────────────────────────────────────────────

const card1ImageDef: SlotGroupDefinition = {
  label: 'Card 1 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Select one photo that best represents the apartment interiors or floor plans — a bedroom, living room, kitchen, or model-unit interior shot.',
    context: 'Background of the "Browse Our Floor Plans" navigation tile on the hyperlocal page.',
    sourceCollection: 'photo_gallery',
  },
};

const card2ImageDef: SlotGroupDefinition = {
  label: 'Card 2 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Select one photo of a community amenity — the pool, fitness center, clubhouse, lounge, or another shared amenity space.',
    context: 'Background of the "Explore Our Amenities" navigation tile on the hyperlocal page.',
    sourceCollection: 'photo_gallery',
  },
};

const card3ImageDef: SlotGroupDefinition = {
  label: 'Card 3 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Select one photo that showcases the community at large — the building exterior, lobby, courtyard, or a standout lifestyle shot suitable for a gallery teaser.',
    context: 'Background of the "View the Gallery" navigation tile on the hyperlocal page.',
    sourceCollection: 'photo_gallery',
  },
};

function linkDef(label: string, url: string): SlotGroupDefinition {
  return {
    label,
    fields: {
      url: { label: 'URL', type: 'url', defaultValue: url },
      _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
    },
  };
}

const card1LinkDef = linkDef('Card 1 Link', '/floor-plans');
const card2LinkDef = linkDef('Card 2 Link', '/amenities');
const card3LinkDef = linkDef('Card 3 Link', '/gallery');

// ─── Component ──────────────────────────────────────────────────────────────

export function HyperlocalExplore() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();
  const gallery = useGallery();

  const card1Image = useSlotGroup('hyperlocalexplore.card1_image', card1ImageDef);
  const card2Image = useSlotGroup('hyperlocalexplore.card2_image', card2ImageDef);
  const card3Image = useSlotGroup('hyperlocalexplore.card3_image', card3ImageDef);

  const card1Link = useSlotGroup('hyperlocalexplore.card1_link', card1LinkDef);
  const card2Link = useSlotGroup('hyperlocalexplore.card2_link', card2LinkDef);
  const card3Link = useSlotGroup('hyperlocalexplore.card3_link', card3LinkDef);

  // Best distinct related photo per card, with a fallback to any remaining
  // photo so every tile has an image even when keywords don't match.
  const chosen = useMemo<(PhotoGallery | null)[]>(() => {
    const used = new Set<number>();
    return CARD_KEYWORDS.map((keywords) => {
      let bestIdx = -1;
      let bestScore = 0;
      for (let i = 0; i < gallery.length; i++) {
        if (used.has(i)) continue;
        const score = scorePhoto(gallery[i], keywords);
        if (score > bestScore) {
          bestScore = score;
          bestIdx = i;
        }
      }
      if (bestIdx < 0) {
        for (let i = 0; i < gallery.length; i++) {
          if (!used.has(i)) { bestIdx = i; break; }
        }
      }
      if (bestIdx >= 0) used.add(bestIdx);
      return bestIdx >= 0 ? gallery[bestIdx] : null;
    });
  }, [gallery]);

  // Editor override wins; otherwise the keyword-matched gallery photo.
  const img1 = pickImage(card1Image.image) || pickImage(chosen[0]?.image) || '';
  const img2 = pickImage(card2Image.image) || pickImage(chosen[1]?.image) || '';
  const img3 = pickImage(card3Image.image) || pickImage(chosen[2]?.image) || '';
  const alt1 = card1Image.alt || chosen[0]?.alt_text || '';
  const alt2 = card2Image.alt || chosen[1]?.alt_text || '';
  const alt3 = card3Image.alt || chosen[2]?.alt_text || '';

  return (
    <section className="pb-10 md:pb-16 bg-[var(--vibe-color-background)]">
      <div
        ref={sectionRef}
        className="reveal-fade-up hl-explore-grid max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        {/* ── Card 1 · Floor Plans ── */}
        <EditableSlotGroup groupPath="hyperlocalexplore.card1_link" definition={card1LinkDef} className="hl-explore-card-wrap">
          <a
            href={card1Link.url || '/floor-plans'}
            target={card1Link._target || '_self'}
            className="hl-explore-card"
          >
            <EditableSlotGroup groupPath="hyperlocalexplore.card1_image" definition={card1ImageDef} className="hl-explore-media">
              {img1 ? (
                <VibesitesImage
                  url={img1}
                  alt={alt1}
                  sizes="(min-width: 768px) 33vw, 100vw"
                  className="hl-explore-img"
                />
              ) : (
                <div className="hl-explore-img hl-explore-img--empty" />
              )}
            </EditableSlotGroup>
            <span aria-hidden="true" className="hl-explore-overlay" />
            <span className="hl-explore-text">
              <RichTextContent
                path="hyperlocalexplore.card1_heading"
                label="Card 1 Heading"
                defaultValue="<p>Superior Spaces</p>"
                as="span"
                className="hl-explore-heading"
              />
              <RichTextContent
                path="hyperlocalexplore.card1_subheading"
                label="Card 1 Subheading"
                defaultValue="<p>Browse Our Floor Plans</p>"
                as="span"
                className="hl-explore-sub"
              />
            </span>
          </a>
        </EditableSlotGroup>

        {/* ── Card 2 · Amenities ── */}
        <EditableSlotGroup groupPath="hyperlocalexplore.card2_link" definition={card2LinkDef} className="hl-explore-card-wrap">
          <a
            href={card2Link.url || '/amenities'}
            target={card2Link._target || '_self'}
            className="hl-explore-card"
          >
            <EditableSlotGroup groupPath="hyperlocalexplore.card2_image" definition={card2ImageDef} className="hl-explore-media">
              {img2 ? (
                <VibesitesImage
                  url={img2}
                  alt={alt2}
                  sizes="(min-width: 768px) 33vw, 100vw"
                  className="hl-explore-img"
                />
              ) : (
                <div className="hl-explore-img hl-explore-img--empty" />
              )}
            </EditableSlotGroup>
            <span aria-hidden="true" className="hl-explore-overlay" />
            <span className="hl-explore-text">
              <RichTextContent
                path="hyperlocalexplore.card2_heading"
                label="Card 2 Heading"
                defaultValue="<p>Indulgent Details</p>"
                as="span"
                className="hl-explore-heading"
              />
              <RichTextContent
                path="hyperlocalexplore.card2_subheading"
                label="Card 2 Subheading"
                defaultValue="<p>Explore Our Amenities</p>"
                as="span"
                className="hl-explore-sub"
              />
            </span>
          </a>
        </EditableSlotGroup>

        {/* ── Card 3 · Gallery ── */}
        <EditableSlotGroup groupPath="hyperlocalexplore.card3_link" definition={card3LinkDef} className="hl-explore-card-wrap">
          <a
            href={card3Link.url || '/gallery'}
            target={card3Link._target || '_self'}
            className="hl-explore-card"
          >
            <EditableSlotGroup groupPath="hyperlocalexplore.card3_image" definition={card3ImageDef} className="hl-explore-media">
              {img3 ? (
                <VibesitesImage
                  url={img3}
                  alt={alt3}
                  sizes="(min-width: 768px) 33vw, 100vw"
                  className="hl-explore-img"
                />
              ) : (
                <div className="hl-explore-img hl-explore-img--empty" />
              )}
            </EditableSlotGroup>
            <span aria-hidden="true" className="hl-explore-overlay" />
            <span className="hl-explore-text">
              <RichTextContent
                path="hyperlocalexplore.card3_heading"
                label="Card 3 Heading"
                defaultValue="<p>Luxury Living</p>"
                as="span"
                className="hl-explore-heading"
              />
              <RichTextContent
                path="hyperlocalexplore.card3_subheading"
                label="Card 3 Subheading"
                defaultValue="<p>View the Gallery</p>"
                as="span"
                className="hl-explore-sub"
              />
            </span>
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        .hl-explore-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }
        @media (min-width: 768px) {
          .hl-explore-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 1.25rem;
          }
        }
        .hl-explore-card-wrap { display: block; }
        .hl-explore-card {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          aspect-ratio: 3 / 2;
          overflow: hidden;
          border-radius: var(--vibe-radius-sm, 2px);
          text-decoration: none;
        }
        @media (min-width: 768px) {
          .hl-explore-card { aspect-ratio: 4 / 5; }
        }
        .hl-explore-media { position: absolute; inset: 0; }
        .hl-explore-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .hl-explore-img--empty {
          background: color-mix(in srgb, var(--vibe-color-primary) 22%, var(--vibe-color-background));
        }
        .hl-explore-card:hover .hl-explore-img { transform: scale(1.06); }
        .hl-explore-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0.25) 55%, rgba(0,0,0,0.35));
          transition: background 0.4s ease;
        }
        .hl-explore-card:hover .hl-explore-overlay {
          background: linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.45));
        }
        .hl-explore-text {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.6rem;
          text-align: center;
          padding: 1.5rem;
          color: #ffffff;
        }
        .hl-explore-heading {
          display: block;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 2.4vw, 1.875rem);
          font-weight: 400;
          line-height: 1.1;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }
        .hl-explore-sub {
          display: block;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 300;
          letter-spacing: 0.04em;
          opacity: 0.92;
          position: relative;
          padding-bottom: 2px;
        }
        .hl-explore-sub::after {
          content: '';
          position: absolute;
          left: 50%;
          bottom: 0;
          width: 0;
          height: 1px;
          background: currentColor;
          transform: translateX(-50%);
          transition: width 0.35s ease;
        }
        .hl-explore-card:hover .hl-explore-sub::after { width: 100%; }
      `}</style>
    </section>
  );
}

// ─── Content Schema ─────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'HyperlocalExplore',
  displayName: 'Explore Callouts',
  tokens: {
    'hyperlocalexplore.card1_heading': {
      label: 'Card 1 Heading',
      type: 'richtext',
      defaultValue: '<p>Superior Spaces</p>',
    },
    'hyperlocalexplore.card1_subheading': {
      label: 'Card 1 Subheading',
      type: 'richtext',
      defaultValue: '<p>Browse Our Floor Plans</p>',
    },
    'hyperlocalexplore.card2_heading': {
      label: 'Card 2 Heading',
      type: 'richtext',
      defaultValue: '<p>Indulgent Details</p>',
    },
    'hyperlocalexplore.card2_subheading': {
      label: 'Card 2 Subheading',
      type: 'richtext',
      defaultValue: '<p>Explore Our Amenities</p>',
    },
    'hyperlocalexplore.card3_heading': {
      label: 'Card 3 Heading',
      type: 'richtext',
      defaultValue: '<p>Luxury Living</p>',
    },
    'hyperlocalexplore.card3_subheading': {
      label: 'Card 3 Subheading',
      type: 'richtext',
      defaultValue: '<p>View the Gallery</p>',
    },
  },
  slotGroups: {
    'hyperlocalexplore.card1_image': {
      label: 'Card 1 Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultValue: '' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
      generator: {
        prompt:
          'Select one photo that best represents the apartment interiors or floor plans — a bedroom, living room, kitchen, or model-unit interior shot.',
        context: 'Background of the "Browse Our Floor Plans" navigation tile on the hyperlocal page.',
        sourceCollection: 'photo_gallery',
      },
    },
    'hyperlocalexplore.card1_link': {
      label: 'Card 1 Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
        _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hyperlocalexplore.card2_image': {
      label: 'Card 2 Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultValue: '' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
      generator: {
        prompt:
          'Select one photo of a community amenity — the pool, fitness center, clubhouse, lounge, or another shared amenity space.',
        context: 'Background of the "Explore Our Amenities" navigation tile on the hyperlocal page.',
        sourceCollection: 'photo_gallery',
      },
    },
    'hyperlocalexplore.card2_link': {
      label: 'Card 2 Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
        _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hyperlocalexplore.card3_image': {
      label: 'Card 3 Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultValue: '' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: '' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
      generator: {
        prompt:
          'Select one photo that showcases the community at large — the building exterior, lobby, courtyard, or a standout lifestyle shot suitable for a gallery teaser.',
        context: 'Background of the "View the Gallery" navigation tile on the hyperlocal page.',
        sourceCollection: 'photo_gallery',
      },
    },
    'hyperlocalexplore.card3_link': {
      label: 'Card 3 Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
        _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(HyperlocalExplore as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HyperlocalExplore;
